/** Decode JWT payload (no signature verification — for UX only; server validates). */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    if (pad) base64 += '='.repeat(4 - pad)
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

export function getJwtExpiryMs(token: string): number | null {
  const p = decodeJwtPayload(token)
  if (!p || typeof p.exp !== 'number') return null
  return p.exp * 1000
}

/** Seconds until JWT expiry (can be negative if already expired). */
export function getJwtSecondsLeft(token: string): number | null {
  const expMs = getJwtExpiryMs(token)
  if (expMs == null) return null
  return Math.floor((expMs - Date.now()) / 1000)
}

/**
 * When to start the "session ending soon" dialog: at most 5 minutes before expiry,
 * or 20% of total token lifetime (min 30s) for short sessions.
 */
export function getSessionWarningThresholdSeconds(payload: Record<string, unknown> | null): number {
  const nowSec = Math.floor(Date.now() / 1000)
  const exp = typeof payload?.exp === 'number' ? payload.exp : 0
  const iat = typeof payload?.iat === 'number' ? payload.iat : 0
  const remaining = Math.max(0, exp - nowSec)

  if (iat > 0 && exp > iat) {
    const total = Math.max(60, exp - iat)
    return Math.min(5 * 60, Math.max(30, Math.floor(total * 0.2)))
  }

  return Math.min(5 * 60, Math.max(30, Math.floor(remaining * 0.5)))
}
